import React, { useState, useRef, Suspense, lazy, useCallback, useEffect } from 'react';
import { Button, Space, Spin } from 'antd';
import { SaveOutlined, ExportOutlined, ImportOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import { useParams, useNavigate } from 'react-router';
import '@excalidraw/excalidraw/index.css';
import BpmnElementLibrary from './BpmnElementLibrary';
import BpmnPropertyPanel from './BpmnPropertyPanel';
import useRequest from '../../utils/useRequest';
import useMessage from '@/hooks/useMessage';

// 隐藏 Excalidraw App-menu 的样式
const hideAppMenuStyles = `
  .App-menu__left {
    display: none !important;
  }
  .App-menu {
    display: none !important;
  }
  .App-toolbar {
    padding-left: 0 !important;
  }
`;

// 注入样式
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = hideAppMenuStyles;
  document.head.appendChild(styleElement);
}

// 懒加载 Excalidraw 组件
const Excalidraw = lazy(() => 
  import('@excalidraw/excalidraw').then(module => ({
    default: module.Excalidraw
  }))
);

const ExcalidrawBpmnEditor = () => {
  const { id: processKey } = useParams();
  const navigate = useNavigate();
  const { request } = useRequest();
  const { success, error } = useMessage();
  
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processData, setProcessData] = useState(null);
  const [bpmnXml, setBpmnXml] = useState(null);
   const [bpmnGroups, setBpmnGroups] = useState(new Map()); // 使用状态管理BPMN组信息
  
  // 用于跟踪选择状态变化和双击检测
  const prevSelectedIds = useRef({});
  const clickCount = useRef(0);
  const clickTimer = useRef(null);
  const lastClickTime = useRef(0);

  // 加载流程数据
  const loadProcessData = useCallback(async () => {
    if (!processKey) return;
    
    setLoading(true);
    try {
      // 获取流程定义的XML内容
      const xmlResult = await request(
        `http://127.0.0.1:8080/api/v1/processes/definitions/${processKey}/xml`,
        "GET"
      );
      
      if (xmlResult?.success && xmlResult?.data) {
        // 解码Unicode转义字符
        const xmlContent = xmlResult.data.replace(/\\u([0-9a-fA-F]{4})/g, (match, code) => {
          return String.fromCharCode(parseInt(code, 16));
        });
        
        setBpmnXml(xmlContent);
        setProcessData({
          key: processKey,
          xml: xmlContent
        });
        
        // 解析BPMN XML并转换为Excalidraw元素
        const parsedElements = parseBpmnXmlToElements(xmlContent);
        setElements(parsedElements);
        
        // 延迟更新场景，确保Excalidraw API已准备就绪
        setTimeout(() => {
          if (excalidrawAPI && parsedElements.length > 0) {
            excalidrawAPI.updateScene({ elements: parsedElements });
          }
        }, 100);
        
        success('流程数据加载成功');
      } else {
        error('无法获取流程数据');
      }
    } catch (error) {
      console.error('加载流程数据失败:', error);
      error('加载流程数据失败');
    } finally {
      setLoading(false);
    }
  }, [processKey, request]);

  // 解析BPMN XML并转换为Excalidraw元素（支持组管理）
  const parseBpmnXmlToElements = useCallback((xmlContent) => {
    const elements = [];
    const bpmnGroups = new Map(); // 存储BPMN组信息
    
    try {
      // 解析XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(xmlContent, 'text/xml');
      
      // 检查解析错误
      const parseError = xmlDoc.querySelector('parsererror');
      if (parseError) {
        throw new Error('XML解析失败: ' + parseError.textContent);
      }
      
      // 获取BPMN命名空间
      const bpmnNS = 'http://www.omg.org/spec/BPMN/20100524/MODEL';
      const bpmndiNS = 'http://www.omg.org/spec/BPMN/20100524/DI';
      const dcNS = 'http://www.omg.org/spec/DD/20100524/DC';
      
      // 获取流程定义
      const process = xmlDoc.getElementsByTagNameNS(bpmnNS, 'process')[0];
      if (!process) {
        throw new Error('未找到BPMN流程定义');
      }
      
      // 获取图形信息
      const bpmnDiagram = xmlDoc.getElementsByTagNameNS(bpmndiNS, 'BPMNDiagram')[0];
      const bpmnPlane = bpmnDiagram?.getElementsByTagNameNS(bpmndiNS, 'BPMNPlane')[0];
      
      // 创建元素映射
      const elementMap = new Map();
      const shapeMap = new Map();
      
      // 解析图形位置信息
      if (bpmnPlane) {
        const shapes = bpmnPlane.getElementsByTagNameNS(bpmndiNS, 'BPMNShape');
        for (let shape of shapes) {
          const bpmnElement = shape.getAttribute('bpmnElement');
          const bounds = shape.getElementsByTagNameNS(dcNS, 'Bounds')[0];
          if (bounds) {
            shapeMap.set(bpmnElement, {
              x: parseFloat(bounds.getAttribute('x')),
              y: parseFloat(bounds.getAttribute('y')),
              width: parseFloat(bounds.getAttribute('width')),
              height: parseFloat(bounds.getAttribute('height'))
            });
          }
        }
      }
      
      // 创建BPMN节点组的辅助函数
      const createBpmnNodeGroup = (bpmnId, bpmnType, bpmnName, shape, textElement = null, additionalElements = []) => {
        const groupId = `bpmn-group-${bpmnId}`;
        
        // 设置组ID
        shape.groupIds = [groupId];
        if (textElement) {
          textElement.groupIds = [groupId];
        }
        
        // 为额外元素设置组ID
        additionalElements.forEach(element => {
          if (element) {
            element.groupIds = [groupId];
          }
        });
        
        // 构建所有元素数组
        const allElements = [shape];
        if (textElement) {
          allElements.push(textElement);
        }
        allElements.push(...additionalElements.filter(Boolean));
        
        // 存储组信息到局部Map变量
        bpmnGroups.set(groupId, {
          bpmnId,
          bpmnType,
          bpmnName,
          elements: allElements,
          properties: {}
        });
        
        return groupId;
      };
      
      // 解析开始事件
      const startEvents = process.getElementsByTagNameNS(bpmnNS, 'startEvent');
      for (let startEvent of startEvents) {
        const id = startEvent.getAttribute('id');
        const name = startEvent.getAttribute('name') || '开始事件';
        const shape = shapeMap.get(id);
        
        if (shape) {
          // 创建形状元素
          const shapeElement = {
            id: `start-event-${id}`,
            type: 'ellipse',
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            angle: 0,
            strokeColor: '#52c41a',
            backgroundColor: '#52c41a20',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: { type: 3 },
            index: `a${elements.length}`,
            customData: {
              bpmnType: 'start-event',
              bpmnId: id,
              bpmnName: name,
              name: name,
              properties: {}
            }
          };
          
          // 创建文本标签
          const textElement = name.trim() ? {
            id: `text-start-event-${id}`,
            type: 'text',
            x: shape.x + shape.width / 2 - 30,
            y: shape.y + shape.height + 5,
            width: 60,
            height: 20,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length + 1}`,
            text: name,
            fontSize: 12,
            fontFamily: 1,
            textAlign: 'center',
            verticalAlign: 'middle',
            containerId: null,
            originalText: name,
            autoResize: true,
            lineHeight: 1.25
          } : null;
          
          // 创建BPMN组
          createBpmnNodeGroup(id, 'start-event', name, shapeElement, textElement);
          
          // 添加元素到数组
          elements.push(shapeElement);
          if (textElement) {
            elements.push(textElement);
          }
          elementMap.set(id, shapeElement);
        }
      }
      
      // 解析用户任务
      const userTasks = process.getElementsByTagNameNS(bpmnNS, 'userTask');
      for (let userTask of userTasks) {
        const id = userTask.getAttribute('id');
        const name = userTask.getAttribute('name') || '用户任务';
        const shape = shapeMap.get(id);
        
        // 解析 guanshi 扩展元素
        const guanshiNS = 'http://guanshi.com/schema/bpmn/1.0';
        let assignmentConfig = null;
        let taskSchedule = { due_date: null, follow_up_date: null };
        let formDefinition = null;
        
        // 查找 extensionElements
        const extensionElements = userTask.getElementsByTagNameNS(bpmnNS, 'extensionElements')[0];
        if (extensionElements) {
          // 解析 assignmentDefinition
          const assignmentDef = extensionElements.getElementsByTagNameNS(guanshiNS, 'assignmentDefinition')[0];
          if (assignmentDef) {
            const candidateAttr = assignmentDef.getAttribute('candidate');
            if (candidateAttr) {
              try {
                // 解码 HTML 实体并解析 JSON
                const decodedCandidate = candidateAttr
                  .replace(/&quot;/g, '"')
                  .replace(/&amp;/g, '&')
                  .replace(/&lt;/g, '<')
                  .replace(/&gt;/g, '>');
                assignmentConfig = JSON.parse(decodedCandidate);
              } catch (error) {
                console.warn('解析 assignmentDefinition candidate 失败:', error);
              }
            }
          }
          
          // 解析 taskSchedule
          const taskScheduleDef = extensionElements.getElementsByTagNameNS(guanshiNS, 'taskSchedule')[0];
          if (taskScheduleDef) {
            const dueDate = taskScheduleDef.getAttribute('dueDate');
            const followUpDate = taskScheduleDef.getAttribute('followUpDate');
            taskSchedule = {
              due_date: dueDate || null,
              follow_up_date: followUpDate || null
            };
          }
          
          // 解析 formDefinition
          const formDef = extensionElements.getElementsByTagNameNS(guanshiNS, 'formDefinition')[0];
          if (formDef) {
            const externalRef = formDef.getAttribute('externalReference');
            if (externalRef && externalRef !== 'null') {
              try {
                formDefinition = JSON.parse(externalRef);
              } catch (error) {
                formDefinition = externalRef;
              }
            }
          }
        }
        
        if (shape) {
          // 创建形状元素
          const shapeElement = {
            id: `${id}`,
            type: 'rectangle',
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            angle: 0,
            strokeColor: '#1890ff',
            backgroundColor: '#1890ff20',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: { type: 1 },
            index: `a${elements.length}`,
            customData: {
              bpmnType: 'user-task',
              bpmnId: id,
              bpmnName: name,
              name: name,
              assignmentConfig: assignmentConfig,
              due_date: taskSchedule.due_date,
              follow_up_date: taskSchedule.follow_up_date,
              formKey: formDefinition,
              properties: {}
            }
          };
          
          // 创建文本标签
          const textElement = name.trim() ? {
            id: `text-user-task-${id}`,
            type: 'text',
            x: shape.x + shape.width / 2 - 30,
            y: shape.y + shape.height / 2 - 10,
            width: 60,
            height: 20,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length + 1}`,
            text: name,
            fontSize: 12,
            fontFamily: 1,
            textAlign: 'center',
            verticalAlign: 'middle',
            containerId: null,
            originalText: name,
            autoResize: true,
            lineHeight: 1.25
          } : null;
          
          // 创建BPMN组
          createBpmnNodeGroup(id, 'user-task', name, shapeElement, textElement);
          
          // 添加元素到数组
          elements.push(shapeElement);
          if (textElement) {
            elements.push(textElement);
          }
          elementMap.set(id, shapeElement);
        }
      }
      
      // 解析排他网关
      const exclusiveGateways = process.getElementsByTagNameNS(bpmnNS, 'exclusiveGateway');
      for (let gateway of exclusiveGateways) {
        const id = gateway.getAttribute('id');
        const name = gateway.getAttribute('name') || '排他网关';
        const shape = shapeMap.get(id);
        
        if (shape) {
          // 创建形状元素
          const shapeElement = {
            id: `exclusive-gateway-${id}`,
            type: 'diamond',
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            angle: 0,
            strokeColor: '#faad14',
            backgroundColor: '#faad1420',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length}`,
            customData: {
              bpmnType: 'exclusive-gateway',
              bpmnId: id,
              bpmnName: name,
              name: name,
              properties: {}
            }
          };
          
          // 创建文本标签（网关通常显示在下方）
          const textElement = name.trim() && name !== '排他网关' ? {
            id: `text-exclusive-gateway-${id}`,
            type: 'text',
            x: shape.x + shape.width / 2 - 30,
            y: shape.y + shape.height + 5,
            width: 60,
            height: 20,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length + 1}`,
            text: name,
            fontSize: 12,
            fontFamily: 1,
            textAlign: 'center',
            verticalAlign: 'middle',
            containerId: null,
            originalText: name,
            autoResize: true,
            lineHeight: 1.25
          } : null;
          
          // 创建排他网关内部X符号
          const xLine1 = {
            id: `x-line1-${id}`,
            type: 'line',
            x: shape.x + shape.width * 0.3,
            y: shape.y + shape.height * 0.3,
            width: shape.width * 0.4,
            height: shape.height * 0.4,
            angle: 0,
            strokeColor: '#faad14',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: null,
            index: `a${elements.length + 2}`,
            startBinding: null,
            endBinding: null,
            lastCommittedPoint: null,
            startArrowhead: null,
            endArrowhead: null,
            points: [[0, 0], [shape.width * 0.4, shape.height * 0.4]]
          };
          
          const xLine2 = {
            id: `x-line2-${id}`,
            type: 'line',
            x: shape.x + shape.width * 0.7,
            y: shape.y + shape.height * 0.3,
            width: shape.width * 0.4,
            height: shape.height * 0.4,
            angle: 0,
            strokeColor: '#faad14',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: null,
            index: `a${elements.length + 3}`,
            startBinding: null,
            endBinding: null,
            lastCommittedPoint: null,
            startArrowhead: null,
            endArrowhead: null,
            points: [[0, 0], [-shape.width * 0.4, shape.height * 0.4]]
          };
          
          // 创建BPMN组
          createBpmnNodeGroup(id, 'exclusive-gateway', name, shapeElement, textElement, [xLine1, xLine2]);
          
          // 添加元素到数组
          elements.push(shapeElement, xLine1, xLine2);
          if (textElement) {
            elements.push(textElement);
          }
          elementMap.set(id, shapeElement);
        }
      }
      
      // 解析并行网关
      const parallelGateways = process.getElementsByTagNameNS(bpmnNS, 'parallelGateway');
      for (let gateway of parallelGateways) {
        const id = gateway.getAttribute('id');
        const name = gateway.getAttribute('name') || '并行网关';
        const shape = shapeMap.get(id);
        
        if (shape) {
          // 创建形状元素
          const shapeElement = {
            id: `parallel-gateway-${id}`,
            type: 'diamond',
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            angle: 0,
            strokeColor: '#52c41a',
            backgroundColor: '#52c41a20',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length}`,
            customData: {
              bpmnType: 'parallel-gateway',
              bpmnId: id,
              bpmnName: name,
              name: name,
              properties: {}
            }
          };
          
          // 创建文本标签（网关通常显示在下方）
          const textElement = name.trim() && name !== '并行网关' ? {
            id: `text-parallel-gateway-${id}`,
            type: 'text',
            x: shape.x + shape.width / 2 - 30,
            y: shape.y + shape.height + 5,
            width: 60,
            height: 20,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length + 1}`,
            text: name,
            fontSize: 12,
            fontFamily: 1,
            textAlign: 'center',
            verticalAlign: 'middle',
            containerId: null,
            originalText: name,
            autoResize: true,
            lineHeight: 1.25
          } : null;
          
          // 创建并行网关内部十字符号
          const crossLineH = {
            id: `cross-h-${id}`,
            type: 'line',
            x: shape.x + shape.width * 0.25,
            y: shape.y + shape.height * 0.5,
            width: shape.width * 0.5,
            height: 0,
            angle: 0,
            strokeColor: '#52c41a',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: null,
            index: `a${elements.length + 2}`,
            startBinding: null,
            endBinding: null,
            lastCommittedPoint: null,
            startArrowhead: null,
            endArrowhead: null,
            points: [[0, 0], [shape.width * 0.5, 0]]
          };
          
          const crossLineV = {
            id: `cross-v-${id}`,
            type: 'line',
            x: shape.x + shape.width * 0.5,
            y: shape.y + shape.height * 0.25,
            width: 0,
            height: shape.height * 0.5,
            angle: 0,
            strokeColor: '#52c41a',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: null,
            index: `a${elements.length + 3}`,
            startBinding: null,
            endBinding: null,
            lastCommittedPoint: null,
            startArrowhead: null,
            endArrowhead: null,
            points: [[0, 0], [0, shape.height * 0.5]]
          };
          
          // 创建BPMN组
          createBpmnNodeGroup(id, 'parallel-gateway', name, shapeElement, textElement, [crossLineH, crossLineV]);
          
          // 添加元素到数组
          elements.push(shapeElement, crossLineH, crossLineV);
          if (textElement) {
            elements.push(textElement);
          }
          elementMap.set(id, shapeElement);
        }
      }
      
      // 解析包容网关
      const inclusiveGateways = process.getElementsByTagNameNS(bpmnNS, 'inclusiveGateway');
      for (let gateway of inclusiveGateways) {
        const id = gateway.getAttribute('id');
        const name = gateway.getAttribute('name') || '包容网关';
        const shape = shapeMap.get(id);
        
        if (shape) {
          // 创建形状元素
          const shapeElement = {
            id: `inclusive-gateway-${id}`,
            type: 'diamond',
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            angle: 0,
            strokeColor: '#1890ff',
            backgroundColor: '#1890ff20',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length}`,
            customData: {
              bpmnType: 'inclusive-gateway',
              bpmnId: id,
              bpmnName: name,
              name: name,
              properties: {}
            }
          };
          
          // 创建文本标签（网关通常显示在下方）
          const textElement = name.trim() && name !== '包容网关' ? {
            id: `text-inclusive-gateway-${id}`,
            type: 'text',
            x: shape.x + shape.width / 2 - 30,
            y: shape.y + shape.height + 5,
            width: 60,
            height: 20,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length + 1}`,
            text: name,
            fontSize: 12,
            fontFamily: 1,
            textAlign: 'center',
            verticalAlign: 'middle',
            containerId: null,
            originalText: name,
            autoResize: true,
            lineHeight: 1.25
          } : null;
          
          // 创建包容网关的圆圈符号（O符号）
          const circleElement = {
            id: `circle-${id}`,
            type: 'ellipse',
            x: shape.x + shape.width * 0.3,
            y: shape.y + shape.height * 0.3,
            width: shape.width * 0.4,
            height: shape.height * 0.4,
            angle: 0,
            strokeColor: '#1890ff',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: { type: 3 },
            index: `a${elements.length + 2}`,
            startBinding: null,
            endBinding: null,
            lastCommittedPoint: null,
            startArrowhead: null,
            endArrowhead: null
          };
          
          // 创建BPMN组
          createBpmnNodeGroup(id, 'inclusive-gateway', name, shapeElement, textElement, [circleElement]);
          
          // 添加元素到数组
          elements.push(shapeElement, circleElement);
          if (textElement) {
            elements.push(textElement);
          }
          elementMap.set(id, shapeElement);
        }
      }
      
      // 解析结束事件
      const endEvents = process.getElementsByTagNameNS(bpmnNS, 'endEvent');
      for (let endEvent of endEvents) {
        const id = endEvent.getAttribute('id');
        const name = endEvent.getAttribute('name') || '结束事件';
        const shape = shapeMap.get(id);
        
        if (shape) {
          // 创建形状元素
          const shapeElement = {
            id: `end-event-${id}`,
            type: 'ellipse',
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            angle: 0,
            strokeColor: '#ff4d4f',
            backgroundColor: '#ff4d4f20',
            fillStyle: 'solid',
            strokeWidth: 3,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: { type: 3 },
            index: `a${elements.length}`,
            customData: {
              bpmnType: 'end-event',
              bpmnId: id,
              bpmnName: name,
              name: name,
              properties: {}
            }
          };
          
          // 创建文本标签
          const textElement = name.trim() ? {
            id: `text-end-event-${id}`,
            type: 'text',
            x: shape.x + shape.width / 2 - 30,
            y: shape.y + shape.height + 5,
            width: 60,
            height: 20,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length + 1}`,
            text: name,
            fontSize: 12,
            fontFamily: 1,
            textAlign: 'center',
            verticalAlign: 'middle',
            containerId: null,
            originalText: name,
            autoResize: true,
            lineHeight: 1.25
          } : null;
          
          // 创建BPMN组
          createBpmnNodeGroup(id, 'end-event', name, shapeElement, textElement);
          
          // 添加元素到数组
          elements.push(shapeElement);
          if (textElement) {
            elements.push(textElement);
          }
          elementMap.set(id, shapeElement);
        }
      }
      
      // 解析子流程
      const subProcesses = process.getElementsByTagNameNS(bpmnNS, 'subProcess');
      for (let subProcess of subProcesses) {
        const id = subProcess.getAttribute('id');
        const name = subProcess.getAttribute('name') || '子流程';
        const shape = shapeMap.get(id);
        
        if (shape) {
          // 创建形状元素
          const shapeElement = {
            id: `subprocess-${id}`,
            type: 'rectangle',
            x: shape.x,
            y: shape.y,
            width: shape.width,
            height: shape.height,
            angle: 0,
            strokeColor: '#13c2c2',
            backgroundColor: '#13c2c220',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: { type: 1 },
            index: `a${elements.length}`,
            customData: {
              bpmnType: 'subprocess',
              bpmnId: id,
              bpmnName: name,
              name: name,
              properties: {}
            }
          };
          
          // 创建文本标签
          const textElement = name.trim() ? {
            id: `text-subprocess-${id}`,
            type: 'text',
            x: shape.x + shape.width / 2 - 30,
            y: shape.y + shape.height / 2 - 10,
            width: 60,
            height: 20,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [], // 将在createBpmnNodeGroup中设置
            frameId: null,
            roundness: null,
            index: `a${elements.length + 1}`,
            text: name,
            fontSize: 12,
            fontFamily: 1,
            textAlign: 'center',
            verticalAlign: 'middle',
            containerId: null,
            originalText: name,
            autoResize: true,
            lineHeight: 1.25
          } : null;
          
          // 创建子流程标识符（左上角的小方框加号）
          const subProcessMarker = {
            id: `subprocess-marker-${id}`,
            type: 'rectangle',
            x: shape.x + 5,
            y: shape.y + shape.height - 20,
            width: 14,
            height: 14,
            angle: 0,
            strokeColor: '#13c2c2',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: { type: 1 },
            index: `a${elements.length + 2}`
          };
          
          // 创建加号的水平线
          const plusLineH = {
            id: `plus-h-${id}`,
            type: 'line',
            x: shape.x + 8,
            y: shape.y + shape.height - 13,
            width: 8,
            height: 0,
            angle: 0,
            strokeColor: '#13c2c2',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: null,
            index: `a${elements.length + 3}`,
            startBinding: null,
            endBinding: null,
            lastCommittedPoint: null,
            startArrowhead: null,
            endArrowhead: null,
            points: [[0, 0], [8, 0]]
          };
          
          // 创建加号的垂直线
          const plusLineV = {
            id: `plus-v-${id}`,
            type: 'line',
            x: shape.x + 12,
            y: shape.y + shape.height - 17,
            width: 0,
            height: 8,
            angle: 0,
            strokeColor: '#13c2c2',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 1,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: null,
            index: `a${elements.length + 4}`,
            startBinding: null,
            endBinding: null,
            lastCommittedPoint: null,
            startArrowhead: null,
            endArrowhead: null,
            points: [[0, 0], [0, 8]]
          };
          
          // 创建BPMN组
          createBpmnNodeGroup(id, 'subprocess', name, shapeElement, textElement, [subProcessMarker, plusLineH, plusLineV]);
          
          // 添加元素到数组
          elements.push(shapeElement, subProcessMarker, plusLineH, plusLineV);
          if (textElement) {
            elements.push(textElement);
          }
          elementMap.set(id, shapeElement);
        }
      }
      
      // 解析序列流（连接线）
      const sequenceFlows = process.getElementsByTagNameNS(bpmnNS, 'sequenceFlow');
      const edgeMap = new Map();
      
      // 获取连接线的图形信息
      if (bpmnPlane) {
        const edges = bpmnPlane.getElementsByTagNameNS(bpmndiNS, 'BPMNEdge');
        for (let edge of edges) {
          const bpmnElement = edge.getAttribute('bpmnElement');
          const waypoints = edge.getElementsByTagNameNS('http://www.omg.org/spec/DD/20100524/DI', 'waypoint');
          const points = [];
          for (let waypoint of waypoints) {
            points.push({
              x: parseFloat(waypoint.getAttribute('x')),
              y: parseFloat(waypoint.getAttribute('y'))
            });
          }
          edgeMap.set(bpmnElement, points);
        }
      }
      
      for (let sequenceFlow of sequenceFlows) {
        const id = sequenceFlow.getAttribute('id');
        const name = sequenceFlow.getAttribute('name') || '';
        const sourceRef = sequenceFlow.getAttribute('sourceRef');
        const targetRef = sequenceFlow.getAttribute('targetRef');
        const waypoints = edgeMap.get(id);
        
        // 提取条件表达式
        const conditionExpression = sequenceFlow.getElementsByTagNameNS(bpmnNS, 'conditionExpression')[0];
        const conditionText = conditionExpression ? conditionExpression.textContent.trim() : '';
        
        const sourceElement = elementMap.get(sourceRef);
        const targetElement = elementMap.get(targetRef);
        
        if (sourceElement && targetElement && waypoints && waypoints.length >= 2) {
          const startPoint = waypoints[0];
          const endPoint = waypoints[waypoints.length - 1];
          
          // 转换waypoints为相对坐标
          const points = waypoints.map((point, index) => {
            if (index === 0) return [0, 0];
            return [point.x - startPoint.x, point.y - startPoint.y];
          });
          
          // 创建连线元素
          const sequenceFlowElement = {
            id: `${id}`,
            type: 'arrow',
            x: startPoint.x,
            y: startPoint.y,
            width: endPoint.x - startPoint.x,
            height: endPoint.y - startPoint.y,
            angle: 0,
            strokeColor: '#000000',
            backgroundColor: 'transparent',
            fillStyle: 'solid',
            strokeWidth: 2,
            strokeStyle: 'solid',
            roughness: 0,
            opacity: 100,
            seed: Math.floor(Math.random() * 2 ** 31),
            versionNonce: Math.floor(Math.random() * 2 ** 31),
            isDeleted: false,
            boundElements: [],
            updated: Date.now(),
            link: null,
            locked: false,
            groupIds: [],
            frameId: null,
            roundness: null,
            index: `a${elements.length}`,
            points: points,
            lastCommittedPoint: null,
            startBinding: { elementId: sourceElement.id, focus: 0.5, gap: 1 },
            endBinding: { elementId: targetElement.id, focus: 0.5, gap: 1 },
            startArrowhead: null,
            endArrowhead: 'arrow',
            customData: {
              bpmnType: 'sequence-flow',
              bpmnId: id,
              bpmnName: name,
              name: name,
              sourceRef: sourceRef,
              targetRef: targetRef,
              conditionExpression: conditionText,
              properties: {}
            }
          };
          
          // 创建连线标签文本元素（如果有名称）
          let textElement = null;
          if (name && name.trim()) {
            const midPoint = {
              x: startPoint.x + (endPoint.x - startPoint.x) / 2,
              y: startPoint.y + (endPoint.y - startPoint.y) / 2
            };
            
            textElement = {
              id: `text-sequence-flow-${id}`,
              type: 'text',
              x: midPoint.x - 30,
              y: midPoint.y - 10,
              width: 60,
              height: 20,
              angle: 0,
              strokeColor: '#000000',
              backgroundColor: 'transparent',
              fillStyle: 'solid',
              strokeWidth: 1,
              strokeStyle: 'solid',
              roughness: 0,
              opacity: 100,
              seed: Math.floor(Math.random() * 2 ** 31),
              versionNonce: Math.floor(Math.random() * 2 ** 31),
              isDeleted: false,
              boundElements: [],
              updated: Date.now(),
              link: null,
              locked: false,
              groupIds: [],
              frameId: null,
              roundness: null,
              index: `a${elements.length + 1}`,
              text: name,
              fontSize: 12,
              fontFamily: 1,
              textAlign: 'center',
              verticalAlign: 'middle',
              containerId: null,
              originalText: name,
              lineHeight: 1.25
            };
          }
          
          // 使用createBpmnNodeGroup创建BPMN组
          createBpmnNodeGroup(id, 'sequence-flow', name || '连线', sequenceFlowElement, textElement);
          
          // 添加元素到数组
          elements.push(sequenceFlowElement);
          if (textElement) {
            elements.push(textElement);
          }
        }
      }
      
      // 将解析出的组信息设置到状态中
      setBpmnGroups(new Map(bpmnGroups));
      
    } catch (error) {
      console.error('解析BPMN XML失败:', error);
      // 如果解析失败，返回一个错误提示元素
      elements.push({
        id: 'error-message',
        type: 'text',
        x: 100,
        y: 100,
        width: 300,
        height: 50,
        angle: 0,
        strokeColor: '#ff4d4f',
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 1,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        seed: Math.floor(Math.random() * 2 ** 31),
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        isDeleted: false,
        boundElements: [],
        updated: Date.now(),
        link: null,
        locked: false,
        groupIds: [],
        frameId: null,
        roundness: null,
        index: 'error',
        text: `BPMN解析失败: ${error.message}`,
        fontSize: 16,
        fontFamily: 1,
        textAlign: 'left',
        verticalAlign: 'top',
        containerId: null,
        originalText: `BPMN解析失败: ${error.message}`,
        autoResize: true,
        lineHeight: 1.25
      });
    }
    
    return elements;
  }, []);

  // 组件挂载时加载数据
  useEffect(() => {
    loadProcessData();
  }, [loadProcessData]);

  // 当excalidrawAPI准备就绪且有元素时，更新场景
  useEffect(() => {
    if (excalidrawAPI && elements.length > 0) {
      // 使用requestAnimationFrame确保在下一帧更新
      requestAnimationFrame(() => {
        try {
          excalidrawAPI.updateScene({ elements });
        } catch (error) {
          console.warn('Scene update error:', error);
        }
      });
    }
  }, [excalidrawAPI, elements.length]);

  // 手动更新场景的方法（避免useEffect导致的循环）
  const updateSceneManually = useCallback(() => {
    if (excalidrawAPI && elements.length > 0) {
      try {
        excalidrawAPI.updateScene({ elements });
      } catch (error) {
        console.warn('Scene update error:', error);
      }
    }
  }, [excalidrawAPI, elements]);

  // 返回到定义页面
  const handleGoBack = useCallback(() => {
    navigate('/bpmn/definitions');
  }, [navigate]);

  // 创建 BPMN 元素（支持组管理）
  const createBpmnElement = useCallback((elementType, x = 300, y = 200) => {
    const elementConfig = {
      'start-event': { shape: 'ellipse', width: 36, height: 36, color: '#52c41a', name: '开始事件' },
      'end-event': { shape: 'ellipse', width: 36, height: 36, color: '#ff4d4f', name: '结束事件' },
      'user-task': { shape: 'rectangle', width: 100, height: 80, color: '#1890ff', name: '用户任务' },
      'service-task': { shape: 'rectangle', width: 100, height: 80, color: '#52c41a', name: '服务任务' },
      'script-task': { shape: 'rectangle', width: 100, height: 80, color: '#fa8c16', name: '脚本任务' },
      'exclusive-gateway': { shape: 'diamond', width: 50, height: 50, color: '#faad14', name: '排他网关' },
      'parallel-gateway': { shape: 'diamond', width: 50, height: 50, color: '#52c41a', name: '并行网关' },
      'inclusive-gateway': { shape: 'diamond', width: 50, height: 50, color: '#1890ff', name: '包容网关' },
      'subprocess': { shape: 'rectangle', width: 120, height: 80, color: '#13c2c2', name: '子流程' }
    };

    const config = elementConfig[elementType] || elementConfig['user-task'];
    const elementId = `${elementType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const groupId = `bpmn-group-${elementId}`;
    
    // 创建形状元素
    const shapeElement = {
      id: elementId,
      type: config.shape,
      x: x - config.width / 2,
      y: y - config.height / 2,
      width: config.width,
      height: config.height,
      angle: 0,
      strokeColor: config.color,
      backgroundColor: config.color + '20',
      fillStyle: 'solid',
      strokeWidth: 2,
      strokeStyle: 'solid',
      roughness: 0,
      opacity: 100,
      seed: Math.floor(Math.random() * 2 ** 31),
      versionNonce: Math.floor(Math.random() * 2 ** 31),
      isDeleted: false,
      boundElements: [],
      updated: Date.now(),
      link: null,
      locked: false,
      groupIds: [groupId],
      frameId: null,
      roundness: config.shape === 'rectangle' ? { type: 1 } : (config.shape === 'ellipse' ? { type: 3 } : null),
      index: `a${Date.now()}`,

      customData: {
        bpmnType: elementType,
        bpmnId: elementId,
        bpmnName: config.name,
        name: config.name,
        properties: {}
      }
    };
    
    // 创建文本标签
    const textElement = {
      id: `text-${elementId}`,
      type: 'text',
      x: x - 30,
      y: config.shape === 'rectangle' ? y - 10 : y + config.height / 2 + 5,
      width: 60,
      height: 20,
      angle: 0,
      strokeColor: '#000000',
      backgroundColor: 'transparent',
      fillStyle: 'solid',
      strokeWidth: 1,
      strokeStyle: 'solid',
      roughness: 0,
      opacity: 100,
      seed: Math.floor(Math.random() * 2 ** 31),
      versionNonce: Math.floor(Math.random() * 2 ** 31),
      isDeleted: false,
      boundElements: [],
      updated: Date.now(),
      link: null,
      locked: false,
      groupIds: [groupId],
      frameId: null,
      roundness: null,
      index: `a${Date.now() + 1}`,
      text: config.name,
      fontSize: 12,
      fontFamily: 1,
      textAlign: 'center',
      verticalAlign: 'middle',
      containerId: null,
      originalText: config.name,
      autoResize: true,
      lineHeight: 1.25
    };
    
    // 为网关类型创建内部符号
    let additionalElements = [];
    
    if (elementType === 'exclusive-gateway') {
      // 创建X符号的两条线（与XML解析保持一致的相对位置计算）
      const xLine1 = {
        id: `x-line1-${elementId}`,
        type: 'line',
        x: (x - config.width / 2) + config.width * 0.3,
        y: (y - config.height / 2) + config.height * 0.3,
        width: config.width * 0.4,
        height: config.height * 0.4,
        angle: 0,
        strokeColor: config.color,
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 2,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        seed: Math.floor(Math.random() * 2 ** 31),
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        isDeleted: false,
        boundElements: [],
        updated: Date.now(),
        link: null,
        locked: false,
        groupIds: [groupId],
        frameId: null,
        roundness: null,
        index: `a${Date.now() + 2}`,
        points: [[0, 0], [config.width * 0.4, config.height * 0.4]],
        lastCommittedPoint: null,
        startBinding: null,
        endBinding: null,
        startArrowhead: null,
        endArrowhead: null
      };
      
      const xLine2 = {
        id: `x-line2-${elementId}`,
        type: 'line',
        x: (x - config.width / 2) + config.width * 0.7,
        y: (y - config.height / 2) + config.height * 0.3,
        width: config.width * 0.4,
        height: config.height * 0.4,
        angle: 0,
        strokeColor: config.color,
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 2,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        seed: Math.floor(Math.random() * 2 ** 31),
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        isDeleted: false,
        boundElements: [],
        updated: Date.now(),
        link: null,
        locked: false,
        groupIds: [groupId],
        frameId: null,
        roundness: null,
        index: `a${Date.now() + 3}`,
        points: [[0, 0], [-config.width * 0.4, config.height * 0.4]],
        lastCommittedPoint: null,
        startBinding: null,
        endBinding: null,
        startArrowhead: null,
        endArrowhead: null
      };
      
      additionalElements = [xLine1, xLine2];
    } else if (elementType === 'parallel-gateway') {
      // 创建十字符号的两条线
      const crossLineH = {
        id: `cross-h-${elementId}`,
        type: 'line',
        x: x - 15,
        y: y,
        width: 30,
        height: 0,
        angle: 0,
        strokeColor: config.color,
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 2,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        seed: Math.floor(Math.random() * 2 ** 31),
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        isDeleted: false,
        boundElements: [],
        updated: Date.now(),
        link: null,
        locked: false,
        groupIds: [groupId],
        frameId: null,
        roundness: null,
        index: `a${Date.now() + 2}`,
        points: [[0, 0], [30, 0]],
        lastCommittedPoint: null,
        startBinding: null,
        endBinding: null,
        startArrowhead: null,
        endArrowhead: null
      };
      
      const crossLineV = {
        id: `cross-v-${elementId}`,
        type: 'line',
        x: x,
        y: y - 15,
        width: 0,
        height: 30,
        angle: 0,
        strokeColor: config.color,
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 2,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        seed: Math.floor(Math.random() * 2 ** 31),
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        isDeleted: false,
        boundElements: [],
        updated: Date.now(),
        link: null,
        locked: false,
        groupIds: [groupId],
        frameId: null,
        roundness: null,
        index: `a${Date.now() + 3}`,
        points: [[0, 0], [0, 30]],
        lastCommittedPoint: null,
        startBinding: null,
        endBinding: null,
        startArrowhead: null,
        endArrowhead: null
      };
      
      additionalElements = [crossLineH, crossLineV];
    } else if (elementType === 'inclusive-gateway') {
      // 创建圆圈符号
      const circle = {
        id: `circle-${elementId}`,
        type: 'ellipse',
        x: x - 12,
        y: y - 12,
        width: 24,
        height: 24,
        angle: 0,
        strokeColor: config.color,
        backgroundColor: 'transparent',
        fillStyle: 'solid',
        strokeWidth: 2,
        strokeStyle: 'solid',
        roughness: 0,
        opacity: 100,
        seed: Math.floor(Math.random() * 2 ** 31),
        versionNonce: Math.floor(Math.random() * 2 ** 31),
        isDeleted: false,
        boundElements: [],
        updated: Date.now(),
        link: null,
        locked: false,
        groupIds: [groupId],
        frameId: null,
        roundness: null,
        index: `a${Date.now() + 2}`
      };
      
      additionalElements = [circle];
    }
    
    // 存储组信息到状态
    setBpmnGroups(prev => {
      const newGroups = new Map(prev);
      newGroups.set(groupId, {
        bpmnId: elementId,
        bpmnType: elementType,
        bpmnName: config.name,
        elements: [shapeElement, textElement, ...additionalElements],
        properties: {}
      });
      return newGroups;
    });

    return { shapeElement, textElement, additionalElements, groupId };
  }, [setBpmnGroups]);

  // 清理函数
  useEffect(() => {
    return () => {
      // 清理定时器
      if (clickTimer.current) {
        clearTimeout(clickTimer.current);
      }
    };
  }, []);

  // 处理从元素库添加元素
  const handleAddElement = useCallback((elementType) => {
    const { shapeElement, textElement, additionalElements, groupId } = createBpmnElement(elementType);
    const newElements = [shapeElement, textElement, ...additionalElements];
    const updatedElements = [...elements, ...newElements];
    setElements(updatedElements);
    
    if (excalidrawAPI) {
      excalidrawAPI.updateScene({ elements: updatedElements });
    }
    
    success(`已添加 ${shapeElement.customData.bpmnName}`);
  }, [elements, excalidrawAPI, createBpmnElement]);

  // 处理画布拖拽放置
  const handleCanvasDrop = useCallback((event) => {
    event.preventDefault();
    const elementType = event.dataTransfer.getData('application/bpmn-element');
    
    if (elementType && excalidrawAPI) {
      const rect = event.currentTarget.getBoundingClientRect();
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      const { shapeElement, textElement, additionalElements, groupId } = createBpmnElement(elementType, x, y);
      const newElements = [shapeElement, textElement, ...additionalElements];
      const updatedElements = [...elements, ...newElements];
      setElements(updatedElements);
      
      excalidrawAPI.updateScene({ elements: updatedElements });
      success(`已添加 ${shapeElement.customData.bpmnName}`);
    }
  }, [elements, excalidrawAPI, createBpmnElement]);

  // 查找指定位置的元素
  const findElementAtPosition = useCallback((x, y) => {
    return elements.find(element => {
      return x >= element.x && x <= element.x + element.width &&
             y >= element.y && y <= element.y + element.height;
    });
  }, [elements]);

  // 处理双击事件
  const handleDoubleClick = useCallback((pointerDownState) => {
    const { x, y } = pointerDownState.origin;
    const clickedElement = findElementAtPosition(x, y);
    
    if (clickedElement && clickedElement.groupIds?.length > 0) {
      const groupId = clickedElement.groupIds[0];
      const groupInfo = bpmnGroups.get(groupId);
      
      if (groupInfo) {
        // 设置选中的BPMN组元素，用于属性面板显示
        setSelectedElement({
          ...clickedElement,
          _bpmnGroup: groupInfo,
          _groupId: groupId,
          customData: {
            ...clickedElement.customData,
            bpmnType: groupInfo.bpmnType,
            bpmnId: groupInfo.bpmnId,
            bpmnName: groupInfo.bpmnName,
            properties: groupInfo.properties
          }
        });
        
        // 根据BPMN元素类型执行不同的双击动作
        switch (groupInfo.bpmnType) {
          case 'user-task':
            // 用户任务双击：打开任务配置对话框
            success(`双击用户任务: ${groupInfo.bpmnName}`);
            console.log('打开用户任务配置:', groupInfo);
            // TODO: 可以在这里打开任务配置模态框
            break;
            
          case 'service-task':
            // 服务任务双击：打开服务配置
            success(`双击服务任务: ${groupInfo.bpmnName}`);
            console.log('打开服务任务配置:', groupInfo);
            // TODO: 可以在这里打开服务配置界面
            break;
            
          case 'script-task':
            // 脚本任务双击：打开脚本编辑器
            success(`双击脚本任务: ${groupInfo.bpmnName}`);
            console.log('打开脚本编辑器:', groupInfo);
            // TODO: 可以在这里打开代码编辑器
            break;
            
          case 'exclusive-gateway':
          case 'parallel-gateway':
          case 'inclusive-gateway':
            // 网关双击：显示条件配置
            success(`双击网关: ${groupInfo.bpmnName}`);
            console.log('配置网关条件:', groupInfo);
            // TODO: 可以在这里打开条件配置界面
            break;
            
          case 'start-event':
            // 开始事件双击：配置触发条件
            success(`双击开始事件: ${groupInfo.bpmnName}`);
            console.log('配置开始事件触发条件:', groupInfo);
            // TODO: 可以在这里配置事件触发器
            break;
            
          case 'end-event':
            // 结束事件双击：配置结束动作
            success(`双击结束事件: ${groupInfo.bpmnName}`);
            console.log('配置结束事件动作:', groupInfo);
            // TODO: 可以在这里配置结束动作
            break;
            
          default:
            // 默认双击动作：显示元素详情
            success(`双击BPMN元素: ${groupInfo.bpmnName}`);
            console.log('双击BPMN元素详情:', groupInfo);
            break;
        }
        
        // 可选：高亮显示双击的元素
        if (excalidrawAPI) {
          try {
            // 临时改变元素样式以提供视觉反馈
            const updatedElements = elements.map(el => {
              if (el.groupIds && el.groupIds.includes(groupId)) {
                return {
                  ...el,
                  strokeWidth: el.strokeWidth + 2,
                  opacity: 80
                };
              }
              return el;
            });
            
            excalidrawAPI.updateScene({ elements: updatedElements });
            
            // 500ms后恢复原样
            setTimeout(() => {
              excalidrawAPI.updateScene({ elements });
            }, 500);
          } catch (error) {
            console.warn('Failed to highlight element:', error);
          }
        }
      }
    } else if (clickedElement) {
      // 双击非BPMN组元素
      success(`双击元素: ${clickedElement.type}`);
      console.log('双击普通元素:', clickedElement);
    } else {
      // 双击空白区域
      console.log('双击空白区域，坐标:', { x, y });
      // TODO: 可以在这里添加快速创建元素的功能
    }
  }, [findElementAtPosition, bpmnGroups, elements, excalidrawAPI, success]);

  // 处理单击事件
  const handleSingleClick = useCallback((pointerDownState) => {
    // 延迟处理选择状态，确保Excalidraw内部状态已更新
    setTimeout(() => {
      if (!excalidrawAPI) return;
      
      try {
        const appState = excalidrawAPI.getAppState();
        const currentElements = excalidrawAPI.getSceneElements();
        
        if (appState && appState.selectedElementIds) {
          const selectedElements = currentElements.filter(el => appState.selectedElementIds[el.id]);
          
          if (selectedElements.length > 0) {
            // 检查是否选中了BPMN组
            const selectedGroups = new Set();
            
            selectedElements.forEach(element => {
              if (element.groupIds && element.groupIds.length > 0) {
                element.groupIds.forEach(groupId => {
                  if (bpmnGroups.has(groupId)) {
                    selectedGroups.add(groupId);
                  }
                });
              }
            });
            
            if (selectedGroups.size === 1) {
              // 选中了单个BPMN组，显示组信息
              const groupId = Array.from(selectedGroups)[0];
              const groupInfo = bpmnGroups.get(groupId);
              setSelectedElement({
                ...selectedElements[0],
                _bpmnGroup: groupInfo,
                _groupId: groupId,
                customData: {
                  ...selectedElements[0].customData,
                  bpmnType: groupInfo.bpmnType,
                  bpmnId: groupInfo.bpmnId,
                  bpmnName: groupInfo.bpmnName,
                  properties: groupInfo.properties
                }
              });
            } else if (selectedElements.length === 1) {
              // 选中了单个元素
              setSelectedElement(selectedElements[0]);
            } else {
              // 选中了多个元素
              setSelectedElement(null);
            }
          } else {
            setSelectedElement(null);
          }
        }
      } catch (error) {
        console.warn('Selection check error:', error);
      }
    }, 50);
  }, [excalidrawAPI, bpmnGroups]);

  // 处理指针按下事件（实现双击检测）
  const handlePointerDown = useCallback((activeTool, pointerDownState, event) => {
    const currentTime = Date.now();
    const timeDiff = currentTime - lastClickTime.current;
    
    // 清除之前的定时器
    if (clickTimer.current) {
      clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    
    if (timeDiff < 300) {
      // 双击检测
      clickCount.current = 2;
      handleDoubleClick(pointerDownState);
    } else {
      // 单击检测，延迟执行以区分双击
      clickCount.current = 1;
      clickTimer.current = setTimeout(() => {
        if (clickCount.current === 1) {
          handleSingleClick(pointerDownState);
        }
        clickCount.current = 0;
      }, 300);
    }
    
    lastClickTime.current = currentTime;
  }, [handleDoubleClick, handleSingleClick]);

  // 处理场景变化事件
  const handleSceneChange = useCallback((elements, appState, files) => {
    // 更新本地元素状态
    setElements(elements);
    
    // 检查选择状态变化并实现组选择逻辑
    if (appState && appState.selectedElementIds && excalidrawAPI) {
      const selectedIds = Object.keys(appState.selectedElementIds).filter(
        id => appState.selectedElementIds[id]
      );
      
      const prevIds = Object.keys(prevSelectedIds.current).filter(
        id => prevSelectedIds.current[id]
      );
      
      // 如果选择状态发生变化，检查是否需要自动选中整个组
      if (JSON.stringify(selectedIds.sort()) !== JSON.stringify(prevIds.sort())) {
        const selectedElements = elements.filter(el => appState.selectedElementIds[el.id]);
        
        // 检查选中的元素是否属于BPMN组
        const bpmnGroupElements = selectedElements.filter(el => 
          el.groupIds?.length > 0 && bpmnGroups.has(el.groupIds[0])
        );
        
        if (bpmnGroupElements.length > 0) {
          // 收集所有涉及的BPMN组
          const involvedGroups = new Set();
          bpmnGroupElements.forEach(el => {
            if (el.groupIds?.length > 0) {
              involvedGroups.add(el.groupIds[0]);
            }
          });
          
          // 对每个组，确保选中组内所有元素
          let needsUpdate = false;
          const newSelectedElementIds = { ...appState.selectedElementIds };
          
          involvedGroups.forEach(groupId => {
            const groupInfo = bpmnGroups.get(groupId);
            if (groupInfo && groupInfo.elements) {
              groupInfo.elements.forEach(groupElement => {
                if (!newSelectedElementIds[groupElement.id]) {
                  newSelectedElementIds[groupElement.id] = true;
                  needsUpdate = true;
                }
              });
            }
          });
          
          // 如果需要更新选择状态，强制选中整个组
          if (needsUpdate) {
            try {
              excalidrawAPI.updateScene({
                elements,
                appState: {
                  ...appState,
                  selectedElementIds: newSelectedElementIds
                }
              });
            } catch (error) {
              console.warn('Failed to update group selection:', error);
            }
          }
          
          // 设置选中的BPMN组信息用于属性面板
          if (involvedGroups.size === 1) {
            const groupId = Array.from(involvedGroups)[0];
            const groupInfo = bpmnGroups.get(groupId);
            const firstElement = selectedElements[0];
            
            setSelectedElement({
              ...firstElement,
              _bpmnGroup: groupInfo,
              _groupId: groupId,
              customData: {
                ...firstElement.customData,
                bpmnType: groupInfo.bpmnType,
                bpmnId: groupInfo.bpmnId,
                bpmnName: groupInfo.bpmnName,
                properties: groupInfo.properties
              }
            });
          } else {
            setSelectedElement(null);
          }
        } else if (selectedElements.length === 1) {
          // 选中了单个非BPMN组元素
          setSelectedElement(selectedElements[0]);
        } else {
          setSelectedElement(null);
        }
        
        prevSelectedIds.current = appState.selectedElementIds;
      }
    }
  }, [bpmnGroups, excalidrawAPI]);

  // 保存流程
  const handleSave = useCallback(() => {
    success('流程已保存');
  }, []);

  // 导出流程
  const handleExport = useCallback(() => {
    success('流程已导出');
  }, []);

  // 更新元素属性（支持组级别更新）
  const handleUpdateElement = useCallback((updatedElement) => {
    let updatedElements = [...elements];
    
    // 检查是否是BPMN组更新
    if (updatedElement._bpmnGroup && updatedElement._groupId) {
      const groupId = updatedElement._groupId;
      
      if (bpmnGroups.has(groupId)) {
        const groupInfo = bpmnGroups.get(groupId);
        
        // 更新组信息
        const updatedGroupInfo = {
          ...groupInfo,
          bpmnName: updatedElement.customData?.name || updatedElement.customData?.bpmnName || groupInfo.bpmnName,
          properties: {
            ...groupInfo.properties,
            ...(updatedElement.customData || {})
          }
        };
        
        // 更新状态中的组信息
        setBpmnGroups(prev => {
          const newGroups = new Map(prev);
          newGroups.set(groupId, updatedGroupInfo);
          return newGroups;
        });
        
        // 更新组内所有元素
        updatedElements = elements.map(el => {
          if (el.groupIds && el.groupIds.includes(groupId)) {
            const updates = { ...el };
            
            // 更新customData
            if (el.customData) {
              updates.customData = {
                ...el.customData,
                bpmnName: updatedGroupInfo.bpmnName,
                name: updatedGroupInfo.bpmnName,
                ...updatedGroupInfo.properties
              };
            }
            
            // 如果是文本元素，更新文本内容
            if (el.type === 'text' && el.id.startsWith('text-')) {
              updates.text = updatedGroupInfo.bpmnName;
              updates.originalText = updatedGroupInfo.bpmnName;
            }
            
            // 应用样式更新
            if (updatedElement.strokeColor) updates.strokeColor = updatedElement.strokeColor;
            if (updatedElement.backgroundColor) updates.backgroundColor = updatedElement.backgroundColor;
            if (updatedElement.strokeWidth) updates.strokeWidth = updatedElement.strokeWidth;
            if (updatedElement.fillStyle) updates.fillStyle = updatedElement.fillStyle;
            if (updatedElement.strokeStyle) updates.strokeStyle = updatedElement.strokeStyle;
            if (updatedElement.opacity !== undefined) updates.opacity = updatedElement.opacity;
            
            return updates;
          }
          return el;
        });
        
        // 更新选中元素状态
        setSelectedElement(prev => {
          if (prev && prev._groupId === groupId) {
            return {
              ...prev,
              _bpmnGroup: updatedGroupInfo,
              customData: {
                ...prev.customData,
                bpmnName: updatedGroupInfo.bpmnName,
                name: updatedGroupInfo.bpmnName,
                ...updatedGroupInfo.properties
              }
            };
          }
          return prev;
        });
      }
    } else {
      // 单个元素更新
      updatedElements = elements.map(el => 
        el.id === updatedElement.id ? updatedElement : el
      );
    }
    
    setElements(updatedElements);
    
    if (excalidrawAPI) {
      excalidrawAPI.updateScene({ elements: updatedElements });
    }
  }, [elements, excalidrawAPI, bpmnGroups]);

  // 删除元素（支持组级别删除）
  const handleDeleteElement = useCallback((elementId) => {
    let updatedElements = [...elements];
    
    // 查找要删除的元素
    const elementToDelete = elements.find(el => el.id === elementId);
    
    if (elementToDelete && elementToDelete.groupIds && elementToDelete.groupIds.length > 0) {
      // 删除整个BPMN组
      const groupId = elementToDelete.groupIds[0];
      
      if (bpmnGroups.has(groupId)) {
        // 删除组内所有元素
        updatedElements = elements.filter(el => 
          !el.groupIds || !el.groupIds.includes(groupId)
        );
        
        // 从状态中删除组信息
        setBpmnGroups(prev => {
          const newGroups = new Map(prev);
          newGroups.delete(groupId);
          return newGroups;
        });
        
        success('已删除 BPMN 节点组');
      }
    } else {
      // 删除单个元素
      updatedElements = elements.filter(el => el.id !== elementId);
      success('已删除元素');
    }
    
    setElements(updatedElements);
    setSelectedElement(null);
    
    if (excalidrawAPI) {
      excalidrawAPI.updateScene({ elements: updatedElements });
    }
  }, [elements, excalidrawAPI, bpmnGroups, success]);

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center" style={{ height: 'calc(100vh - 90px)' }}>
        <Spin size="large" spinning={true}>
          <div className="text-center text-gray-600 mt-4">
            正在加载流程数据...
          </div>
        </Spin>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col" style={{ height: 'calc(100vh - 90px)', width: '100%' }}>
      {/* 顶部工具栏 */}
      <div className="flex items-center justify-between p-4 border-b bg-white">
        <div className="flex items-center space-x-4">
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleGoBack}
            type="text"
            className="mr-2"
          >
            返回
          </Button>
          <div className="border-l border-gray-300 h-6 mx-2" />
          <h1 className="text-lg font-semibold text-gray-900">
            BPMN 流程设计器
          </h1>
          {processKey && (
            <div className="text-sm text-gray-500">
              流程: {processKey}
            </div>
          )}
          <div className="text-sm text-gray-500">
            可视化流程编辑
          </div>
        </div>
        
        <Space>
          <Button icon={<SaveOutlined />} type="primary" onClick={handleSave}>
            保存
          </Button>
          <Button icon={<ExportOutlined />} onClick={handleExport}>
            导出
          </Button>
          <Button icon={<ImportOutlined />}>
            导入
          </Button>
        </Space>
      </div>

      <div className="flex-1 flex" style={{ height: 'calc(100vh - 180px)', width: '100%' }}>
        {/* 左侧 BPMN 元素库 */}
        <div className="w-80 border-r bg-white overflow-y-auto">
          <BpmnElementLibrary onAddElement={handleAddElement} />
        </div>

        {/* 中间 Excalidraw 画布 */}
        <div 
          className="flex-1 relative"
          onDrop={handleCanvasDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <Suspense 
            fallback={
              <div className="h-full w-full flex items-center justify-center">
                <div className="text-lg text-gray-600">Loading Excalidraw...</div>
              </div>
            }
          >
            <Excalidraw
              excalidrawAPI={(api) => setExcalidrawAPI(api)}
              initialData={{
                appState: {
                  viewModeEnabled: false,
                  zenModeEnabled: false,
                  gridModeEnabled: true,
                  theme: 'light',
                  viewBackgroundColor: '#ffffff'
                }
              }}
              onChange={handleSceneChange}
              onPointerDown={handlePointerDown}
              UIOptions={{
                canvasActions: {
                  loadScene: false,
                  saveToActiveFile: false,
                  export: false,
                  toggleTheme: false,
                  saveAsImage: false,
                  clearCanvas: false,
                  changeViewBackgroundColor: false
                },
                tools: {
                  image: true,
                  text: true,
                  rectangle: true,
                  diamond: true,
                  ellipse: true,
                  arrow: true,
                  line: true,
                  freedraw: true,
                  eraser: true
                }
              }}
            />
          </Suspense>
        </div>

        {/* 右侧属性面板 */}
        <div className="w-80 border-l bg-white overflow-y-auto">
          <BpmnPropertyPanel 
            selectedElement={selectedElement}
            onUpdateElement={handleUpdateElement}
            onDeleteElement={handleDeleteElement}
          />
        </div>
      </div>
    </div>
  );
};

export default ExcalidrawBpmnEditor;